import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, TextField, Drawer, styled, IconButton, InputAdornment, Stack, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router';
import CustomModal from '../../CustomModal';
import userLock from '../../../assets/user-lock.png';
import Image from '../../Image';
import useLocales from '../../../hooks/useLocales';
import Iconify from '../../Iconify';
import NonRootHeader from '../../../layouts/dashboard/header/NonRootHeader';
import useResponsive from '../../../hooks/useResponsive';
import Page from '../../Page';
import { ThemeButton } from '../../settings/GlobalStyled';
import { conObjForm } from '../../../utils/connection-assistant';
import { isSafariOrIOS } from '../../../utils/isSafariOrIOS';

const Rule = ({ valid, text }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: valid ? '#16A34A' : '#9CA3AF',
      fontWeight: valid ? 600 : 400,
      transition: 'all 0.2s ease',
    }}
  >
    • {text}
  </Typography>
);

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F3F4F6',
    '& fieldset': { border: '1px solid transparent' },
    '&:hover fieldset': { border: '1px solid transparent' },
    '&.Mui-focused fieldset': { border: '1px solid #D1D5DB' },
    height: '36px',
  },
  '& input': {
    padding: '12px 16px',
    fontSize: '14px',
  },
  '& input::placeholder': {
    color: '#9CA3AF',
    opacity: 1,
  },
};

const ConfirmButton = styled(ThemeButton)({
  // add custom style
  fontWeight: 600,
  height: 36,
  minHeight: 'unset',
});

const ChangePasswordDialog = ({ handleClose, handleOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const { translate } = useLocales();
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const otp = useSelector((state) => state.profile.data);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isMinLength = password.length > 6;
  const hasLetterAndNumber = /[A-Za-z]/.test(password) && /\d/.test(password);
  const passwordsMatch = password && password === confirmPassword;

  const isValid = isMinLength && hasLetterAndNumber && passwordsMatch;

  const handleConfirm = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');

      const payload = {
        'change_password[old_password]': oldPassword,
        'change_password[new_password]': password,
        'change_password[confirm_password]': confirmPassword,
      };

      const { data: response } = await conObjForm.post('v1/user/change-password', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        enqueueSnackbar(response?.message || 'Password changed successfully', {
          variant: 'success',
        });

        handleClose();
        navigate('/');
        return;
      }

      enqueueSnackbar(response?.error || response?.message || 'Something went wrong', { variant: 'error' });
    } catch (error) {
      const data = error.response?.data;

      enqueueSnackbar(data?.error || data?.message || 'An error occurred during submission', { variant: 'error' });

      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          title={translate('change_password')}
          open={handleOpen}
          onClose={handleClose}
          actions={
            <ConfirmButton variant="contained" fullWidth disabled={!isValid || loading} onClick={handleConfirm}>
              {translate('confirm')}
            </ConfirmButton>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2, px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Image src={userLock} style={{ width: 110 }} alt="reset-password" />
            </Box>

            <Typography textAlign={'center'} fontWeight={'bold'} fontSize={'24px'}>
              {translate('change_password')}
            </Typography>
            <Typography textAlign={'center'} fontWeight={500} fontSize={'16px'}>
              {translate('please_enter_new_password')}
            </Typography>

            <TextField
              type={showOldPassword ? 'text' : 'password'}
              placeholder={translate('old_password')}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              fullWidth
              size="small"
              sx={inputStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                      size="small"
                      aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                    >
                      <Iconify icon={showOldPassword ? 'mdi:eye-off' : 'mdi:eye'} width={20} color="#D6D8E1" />
                    </IconButton>

                    {oldPassword && (
                      <IconButton onClick={() => setOldPassword('')} edge="end" size="small" aria-label="Clear input">
                        <Iconify icon="mdi:close-circle" width={20} color="#D6D8E1" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              type={showPassword ? 'text' : 'password'}
              placeholder={translate('enter_new_password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
              sx={inputStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <Iconify icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} width={20} color="#D6D8E1" />
                    </IconButton>

                    {password && (
                      <IconButton onClick={() => setPassword('')} edge="end" size="small" aria-label="Clear input">
                        <Iconify icon="mdi:close-circle" width={20} color="#D6D8E1" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={translate('confirm_new_password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              sx={inputStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <Iconify icon={showConfirmPassword ? 'mdi:eye-off' : 'mdi:eye'} width={20} color="#D6D8E1" />
                    </IconButton>

                    {confirmPassword && (
                      <IconButton
                        onClick={() => setConfirmPassword('')}
                        edge="end"
                        size="small"
                        aria-label="Clear input"
                      >
                        <Iconify icon="mdi:close-circle" width={20} color="#D6D8E1" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="caption">{translate('password_contain')}</Typography>

            <Box sx={{ fontSize: 13 }}>
              <Rule valid={isMinLength} text={translate('six_characters')} />
              <Rule valid={hasLetterAndNumber} text={translate('contains_letters_numbers')} />
            </Box>
          </Box>
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          PaperProps={{
            sx: {
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            },
            margin: 'auto',
            height: { sm: 'calc(100vh - 64px)', xs: 'calc(100% - (64px + 58px))' },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', left: 8 }}>
              <Iconify icon="material-symbols:arrow-back-ios-new-rounded" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {translate('change-password')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // gap: 2,
              // flex: 1,
              height: '100%',
              // overflow: 'hidden',
              background: isSafariOrIOS ? theme.palette.gradients.theme3Ios : theme.palette.gradients.theme3,
            }}
          >
            <Page
              title="Change Password"
              sx={{
                height: 'auto',
                width: '100%',
                // m: "auto"
              }}
            >
              <Box
                sx={{
                  // height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: { xs: '80%', sm: '100%' },
                    maxWidth: { xs: 'unsset', sm: 400 },
                    mx: 'auto',
                    mt: 1,
                    // transform: {
                    //   xs: 'scale(0.78)',
                    //   sm: 'scale(0.85)',
                    //   md: 'scale(1)',
                    // },
                    transformOrigin: 'center center',

                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ p: 2, borderRadius: 1, backgroundColor: 'white' }}>
                    <img
                      src={userLock}
                      alt="login-icon"
                      style={{ width: '30vw', maxWidth: 160, margin: '0 auto 24px', display: 'block' }}
                    />
                    <Stack spacing={2}>
                      <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                        <Typography textAlign={'center'} fontWeight={'bold'} fontSize={'20px'}>
                          {translate('change_password')}
                        </Typography>
                        <Typography textAlign={'center'} fontWeight={500} fontSize={'14px'}>
                          {translate('please_enter_new_password')}
                        </Typography>
                      </Box>

                      <Box display={'flex'} flexDirection={'column'} gap={'10px'}>
                        <TextField
                          type={showOldPassword ? 'text' : 'password'}
                          placeholder={translate('old_password')}
                          value={oldPassword}
                          onChange={(e) => setOldPassword(e.target.value)}
                          fullWidth
                          size="small"
                          sx={inputStyles}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  onClick={() => setShowOldPassword(!showOldPassword)}
                                  edge="end"
                                  size="small"
                                  aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                                >
                                  <Iconify
                                    icon={showOldPassword ? 'mdi:eye-off' : 'mdi:eye'}
                                    width={20}
                                    color="#D6D8E1"
                                  />
                                </IconButton>

                                {oldPassword && (
                                  <IconButton
                                    onClick={() => setOldPassword('')}
                                    edge="end"
                                    size="small"
                                    aria-label="Clear input"
                                  >
                                    <Iconify icon="mdi:close-circle" width={20} color="#D6D8E1" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          type={showPassword ? 'text' : 'password'}
                          placeholder={translate('enter_new_password')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          fullWidth
                          size="small"
                          sx={inputStyles}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  size="small"
                                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                  <Iconify icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} width={20} color="#D6D8E1" />
                                </IconButton>

                                {password && (
                                  <IconButton
                                    onClick={() => setPassword('')}
                                    edge="end"
                                    size="small"
                                    aria-label="Clear input"
                                  >
                                    <Iconify icon="mdi:close-circle" width={20} color="#D6D8E1" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder={translate('confirm_new_password')}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          fullWidth
                          size="small"
                          sx={inputStyles}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                  size="small"
                                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                >
                                  <Iconify
                                    icon={showConfirmPassword ? 'mdi:eye-off' : 'mdi:eye'}
                                    width={20}
                                    color="#D6D8E1"
                                  />
                                </IconButton>

                                {confirmPassword && (
                                  <IconButton
                                    onClick={() => setConfirmPassword('')}
                                    edge="end"
                                    size="small"
                                    aria-label="Clear input"
                                  >
                                    <Iconify icon="mdi:close-circle" width={20} color="#D6D8E1" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Typography variant="caption">{translate('password_contain')}</Typography>

                        <Box sx={{ fontSize: 11 }}>
                          <Rule valid={isMinLength} text={translate('six_characters')} />
                          <Rule valid={hasLetterAndNumber} text={translate('contains_letters_numbers')} />
                        </Box>
                      </Box>

                      <ConfirmButton
                        variant="contained"
                        fullWidth
                        disabled={!isValid || loading}
                        onClick={handleConfirm}
                      >
                        {translate('confirm')}
                      </ConfirmButton>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Page>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default ChangePasswordDialog;
